import React, { useState, useEffect, useRef } from 'react';
import OpenAI from 'openai';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GoogleDocsService } from './services/googleDocsService';
import './App.css';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'ai';
  timestamp: string;
}

interface KeyThought {
  id: number;
  text: string;
  originalMessage: string;
  timestamp: string;
}

// Add this constant at the top of the file, after the interfaces
const QUICK_ACTIONS = [
  {
    id: 'tournament',
    label: 'Tournament Pressure',
    prompt: 'I get really nervous during tournaments, especially on the first tee. How can I handle this better?'
  },
  {
    id: 'focus',
    label: 'Maintain Focus',
    prompt: 'I struggle to stay focused during long rounds. What can I do to maintain concentration?'
  },
  {
    id: 'confidence',
    label: 'Build Confidence',
    prompt: 'I\'ve been playing poorly lately and my confidence is low. How can I rebuild my confidence?'
  },
  {
    id: 'routine',
    label: 'Pre-shot Routine',
    prompt: 'What should I include in my pre-shot routine to stay mentally strong?'
  },
  {
    id: 'recovery',
    label: 'Bounce Back',
    prompt: 'How can I recover mentally after a bad hole or shot?'
  }
];

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

// Helper function to format timestamp
const formatTimestamp = (timestamp: string) => {
  return new Date(timestamp).toLocaleTimeString();
};

// System prompt for the AI coach (fallback if Google Doc fails)
const COACH_SYSTEM_PROMPT = `You are an expert golf mental performance coach with years of experience helping golfers at all levels improve their mental game. Your coaching style is direct, insightful, and action-oriented.

Key principles:
- Focus on controllables and process over results
- Help players develop strong mental routines and resilience
- Provide specific, actionable strategies for mental challenges
- Maintain a positive but realistic tone that pushes players to improve
- Draw from sports psychology and mental performance best practices

When responding:
- Be conversational and direct - talk like a real coach, not a textbook
- Offer specific tools and strategies without over-structuring
- Ask thoughtful questions to promote self-reflection
- Keep responses concise and impactful (200-300 words maximum)
- Address both the emotional and strategic aspects of mental challenges
- Avoid excessive formatting - use natural paragraph flow

You help golfers build confidence, manage pressure, maintain focus, and develop the mental toughness needed to perform their best on the course.`;

// Add avatar components
const CoachAvatar = () => (
  <div className="w-8 h-8 rounded-full bg-golf-green flex items-center justify-center text-white font-bold">
    CG
  </div>
);

const UserAvatar = () => (
  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
    </svg>
  </div>
);

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [keyThoughts, setKeyThoughts] = useState<KeyThought[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExtractingThought, setIsExtractingThought] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(true);
  const [promptError, setPromptError] = useState<string>('');
  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    // Load messages and key thoughts from localStorage on component mount
    const savedMessages = localStorage.getItem('chatMessages');
    const savedThoughts = localStorage.getItem('keyThoughts');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }
    if (savedThoughts) {
      setKeyThoughts(JSON.parse(savedThoughts));
    }
  }, []);

  // Load system prompt from Google Docs
  const loadSystemPrompt = async (forceRefresh = false) => {
    try {
      setIsLoadingPrompt(true);
      setPromptError('');
      
      if (forceRefresh) {
        // Clear cache and fetch fresh content
        localStorage.removeItem('googleDocsCache');
      }
      
      const prompt = await GoogleDocsService.getDocumentContent();
      setSystemPrompt(prompt);
    } catch (error) {
      console.error('Failed to load system prompt:', error);
      setPromptError(error instanceof Error ? error.message : 'Failed to load system prompt');
      // Fallback to empty prompt
      setSystemPrompt('');
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  useEffect(() => {
    loadSystemPrompt();
  }, []);

  useEffect(() => {
    // Save messages and key thoughts to localStorage whenever they change
    localStorage.setItem('chatMessages', JSON.stringify(messages));
    localStorage.setItem('keyThoughts', JSON.stringify(keyThoughts));
  }, [messages, keyThoughts]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const extractKeyThought = async (message: Message) => {
    setIsExtractingThought(true);
    try {
      const completion = await openai.chat.completions.create({
        messages: [
          {
            role: "system" as const,
            content: "Extract the key mental insight or important point from this golf psychology message. Respond with only 3-5 words that capture the essence. Focus on mental aspects, not technical golf advice. Do not include any additional text or explanation."
          },
          {
            role: "user" as const,
            content: message.text
          }
        ],
        model: "gpt-4o-mini",
        temperature: 0.3,
        max_tokens: 20,
      });

      const simplifiedThought = completion.choices[0].message.content?.trim() || "Key insight";
      
      const newThought: KeyThought = {
        id: Date.now(),
        text: simplifiedThought,
        originalMessage: message.text,
        timestamp: new Date().toISOString(),
      };
      
      setKeyThoughts(prev => [...prev, newThought]);
    } catch (error) {
      console.error('Error extracting key thought:', error);
    } finally {
      setIsExtractingThought(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now(),
      text: inputText,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // Get AI response from OpenAI
      const completion = await openai.chat.completions.create({
        messages: [
          {
            role: "system" as const,
            content: (systemPrompt || COACH_SYSTEM_PROMPT) + `

FORMATTING INSTRUCTIONS:
- Use **bold** sparingly for key concepts and important phrases only
- Use *italics* for mental cues, self-talk phrases, and internal dialogue  
- Use bullet points (-) for lists of strategies or action items
- Use numbered lists (1. 2. 3.) only for step-by-step processes or routines
- Keep responses conversational - avoid overusing section headers (### should be rare)
- Structure with natural paragraphs, not formal sections
- Aim for 50-150 words depending on the situation - be concise and impactful
- Sound like a coach talking, not writing a manual
`
          },
          ...messages.map(msg => ({
            role: msg.sender === 'user' ? 'user' as const : 'assistant' as const,
            content: msg.text
          })),
          {
            role: "user" as const,
            content: inputText
          }
        ],
        model: "gpt-4o-mini",
        temperature: 0.7,
        max_tokens: 800,
        top_p: 0.9,
        frequency_penalty: 0.1,
        presence_penalty: 0.1,
      });

      const aiMessage: Message = {
        id: Date.now() + 1,
        text: completion.choices[0].message.content || "I apologize, but I couldn't generate a response.",
        sender: 'ai',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage: Message = {
        id: Date.now() + 1,
        text: "I apologize, but I encountered an error. Please try again.",
        sender: 'ai',
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen h-screen w-screen bg-gradient-to-br from-green-50 via-green-100 to-green-200 flex items-center justify-center pt-0 pb-1 px-0 md:p-4">
      <div className="w-screen h-screen md:w-full md:h-[90vh] md:max-w-7xl flex flex-col md:flex-row md:gap-6 gap-0 m-0">
        {/* Chat Panel */}
        <div className="flex-1 bg-white md:rounded-2xl shadow-2xl overflow-hidden flex flex-col h-full min-h-0 w-screen md:w-auto">
          <div className="bg-gradient-to-r from-golf-green to-green-600 h-14 md:h-[120px] flex items-center justify-center">
            <div className="w-full px-1 md:px-0">
              <div className="flex flex-col md:flex-row items-center md:justify-center md:gap-4 gap-2 w-full">
                <h1 className="hidden md:block text-2xl md:text-3xl font-bold text-white text-center w-full md:w-auto">
                  Mindful Golf AI Coach
                </h1>
                <div className="flex flex-row md:flex-row gap-2 md:gap-2 w-full md:w-auto justify-center md:justify-start">
                  <button
                    onClick={() => {
                      setMessages([]);
                      setKeyThoughts([]);
                      localStorage.removeItem('chatMessages');
                      localStorage.removeItem('keyThoughts');
                    }}
                    className="flex-1 md:flex-none px-3 py-2 md:px-4 md:py-2 text-xs md:text-sm bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Clear
                  </button>
                  <button
                    onClick={() => loadSystemPrompt(true)}
                    disabled={isLoadingPrompt}
                    className="flex-1 md:flex-none px-3 py-2 md:px-4 md:py-2 text-xs md:text-sm bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                    title="Refresh coach instructions from Google Docs"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    Refresh Prompt
                  </button>
                </div>
              </div>
              <p className="hidden md:block text-green-100 mt-1 md:mt-2 text-xs md:text-base text-center">
                Your personal mental game coach
              </p>
              {/* Prompt loading indicator */}
              {isLoadingPrompt && (
                <div className="flex items-center justify-center gap-2 mt-2 text-green-100 text-xs md:text-sm">
                  <div className="w-3 h-3 bg-green-100 rounded-full animate-pulse"></div>
                  <span>Loading coach instructions...</span>
                </div>
              )}
              {promptError && (
                <div className="mt-2 text-red-200 text-xs md:text-sm bg-red-500/20 px-2 md:px-3 py-1 rounded-lg">
                  ⚠️ {promptError}
                  <br />
                  <span className="text-xs">Make sure your Google Document is shared with "Anyone with the link can view" permissions.</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-1 md:p-6 bg-gray-50 min-h-0">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 mt-8 space-y-4">
                <div className="text-4xl mb-4">🏌️‍♂️</div>
                <p className="text-lg">Welcome to your mental game coaching session!</p>
                <p className="text-sm">Share your golf mental game challenges and I'll help you develop a stronger mindset.</p>
              </div>
            )}
            <div className="space-y-6">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start gap-3 ${
                    message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
                  } animate-fade-in`}
                >
                  {message.sender === 'user' ? <UserAvatar /> : <CoachAvatar />}
                  <div className="flex flex-col max-w-[80%]">
                    <div
                      className={`p-4 rounded-2xl shadow-sm ${
                        message.sender === 'user'
                          ? 'bg-gradient-to-r from-golf-green to-green-600 text-white rounded-tr-none'
                          : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                      }`}
                    >
                      {message.sender === 'ai' ? (
                        <div className="prose prose-sm max-w-none">
                          <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            components={{
                              // Custom styling for markdown elements
                              strong: ({children}) => <strong className="font-bold text-gray-900">{children}</strong>,
                              em: ({children}) => <em className="italic text-gray-800">{children}</em>,
                              ul: ({children}) => <ul className="list-disc list-outside ml-4 space-y-1 my-2">{children}</ul>,
                              ol: ({children}) => <ol className="list-decimal list-outside ml-4 space-y-1 my-2">{children}</ol>,
                              li: ({children}) => <li className="text-gray-800 pl-1">{children}</li>,
                              p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                              h3: ({children}) => <h3 className="font-semibold text-gray-900 mt-3 mb-1">{children}</h3>,
                              h4: ({children}) => <h4 className="font-medium text-gray-900 mt-2 mb-1">{children}</h4>,
                              blockquote: ({children}) => <blockquote className="border-l-4 border-golf-green pl-3 italic text-gray-700 my-2">{children}</blockquote>,
                            }}
                          >
                            {message.text}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        message.text
                      )}
                    </div>
                    <div className={`text-xs text-gray-500 mt-1 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}>
                      {formatTimestamp(message.timestamp)}
                    </div>
                    {message.sender === 'ai' && (
                      <button
                        onClick={() => extractKeyThought(message)}
                        disabled={isExtractingThought}
                        className="text-xs text-golf-green hover:text-green-700 mt-1 text-left transition-colors duration-200"
                      >
                        {isExtractingThought ? 'Extracting...' : 'Save Key Insight'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex items-center justify-center text-gray-500 space-x-2 animate-pulse">
                  <div className="w-2 h-2 bg-golf-green rounded-full"></div>
                  <div className="w-2 h-2 bg-golf-green rounded-full animation-delay-200"></div>
                  <div className="w-2 h-2 bg-golf-green rounded-full animation-delay-400"></div>
                  <span className="ml-2">Coach is thinking...</span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="p-1 md:p-6 border-t border-gray-200 bg-white">
            {/* Quick Actions */}
            <div className="mb-2 md:mb-4">
              <div className="text-xs md:text-sm text-gray-500 mb-1 md:mb-2">Quick Actions:</div>
              <div className="flex flex-wrap gap-2">
                {QUICK_ACTIONS.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => {
                      setInputText(action.prompt);
                    }}
                    className="px-2 py-1 md:px-3 md:py-1.5 text-xs md:text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200 hover:shadow-sm flex items-center gap-1.5"
                  >
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 md:gap-3">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isLoading && !isLoadingPrompt && handleSendMessage()}
                placeholder={isLoadingPrompt ? "Loading coach instructions..." : "Share your mental game challenges or questions..."}
                className="flex-1 p-2 md:p-3 border border-gray-300 rounded-xl focus:outline-none focus:border-golf-green focus:ring-2 focus:ring-green-100 transition-all duration-200 text-xs md:text-base"
                disabled={isLoading || isLoadingPrompt}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || isLoadingPrompt}
                className={`px-4 py-2 md:px-6 md:py-3 rounded-xl transition-all duration-200 ${
                  isLoading || isLoadingPrompt
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-golf-green to-green-600 hover:from-green-600 hover:to-golf-green text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                }`}
              >
                {isLoadingPrompt ? 'Loading...' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {/* Key Thoughts Panel */}
        <div className="w-full md:w-96 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col mt-4 md:mt-0 hidden md:flex">
          <div className="bg-gradient-to-r from-golf-green to-green-600 h-[120px] flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">
                Key Insights
              </h2>
              <p className="text-green-100 mt-1 text-sm">
                Save important mental game concepts
              </p>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {keyThoughts.length === 0 ? (
              <div className="text-center text-gray-500 mt-8 space-y-4">
                <div className="text-4xl mb-4">💭</div>
                <p className="text-sm">No insights saved yet.</p>
                <p className="text-sm">Click "Save Key Insight" on any coach message to save important mental game concepts.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {keyThoughts.map((thought) => (
                  <div
                    key={thought.id}
                    className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="font-medium text-golf-green text-lg">
                      {thought.text}
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      {formatTimestamp(thought.timestamp)}
                    </div>
                    <div className="text-sm text-gray-600 mt-3 italic border-t border-gray-100 pt-3">
                      "{thought.originalMessage.substring(0, 100)}..."
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App; 